import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import ModelContextEditor from '../lego/ModelContextEditor';
import TrimSpec from '../lego/trims/TrimSpec';
import { ImagePreview } from '../images/ImagePreview';
import BadgeId from '../common/BadgeId';
import { MiniatureStateSwitch, miniatureImageUrl, STATE_COLORS } from './miniatureUiUtils';
import LegoContextSummary from '../lego/LegoContextSummary';
import ScaledMiniature, { computeMiniaturesScale } from './ScaledMiniature';
import { IconButton } from '../common/IconButton';

/**
 * Searchable picker of model_miniatures. Filters by context (modelId / make prefix) and state,
 * shows a thumbnail grid, and calls onSelect(miniatureId) when one is chosen.
 *
 * @param {{ service: object, defaultModelId?: string, currentId?: string, onSelect: function }} props
 *   `service` is the feathers client for '/services/data/model-miniatures'.
 */
export default function MiniatureFinder({ service, defaultModelId = null, currentId = null, onSelect }) {
  const [context, setContext] = useState(defaultModelId ? { modelId: defaultModelId } : null);
  const [states, setStates] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const fetchResults = async () => {
    setLoading(true);
    setError(null);
    try {
      const query = { $sort: { updatedAt: -1 }, $limit: 80 };
      if (context?.modelId) {
        query['context.modelId'] = context.modelId;
      } else if (context?.make) {
        query['context.modelId'] = { $regex: '^' + context.make };
      }
      if (states.length === 1) query.state = states[0];
      else if (states.length > 1) query.state = { $in: states };

      const data = await service.find({ query });
      // Sort by the first context's genId ("@<modelId>@<gen>"): model first, then numeric gen order.
      const sorted = _.sortBy(data || [], [
        (m) => m.context?.[0]?.modelId,
        (m) => {
          const gen = (m.context?.[0]?.genId || '').split('@')[2];
          return /^\d+$/.test(gen) ? parseInt(gen) : 0;
        },
      ]);
      if (mounted.current) setResults(sorted);
    } catch (e) {
      if (mounted.current) setError(e?.message || String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  // Refetch on mount and whenever the filters change.
  useEffect(() => { fetchResults(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
    [JSON.stringify(context), JSON.stringify(states)]);


  const pxPerMm = computeMiniaturesScale(results, { maxHeight: 200, maxWidth: 190 });;

  return <div style={{ minWidth: '720px' }}>
    <div className={'d-flex align-items-center mb-2'}>
      <span style={{ width: '350px', display: 'inline-block' }} className={'zoom-75 mr-2'}>
        <ModelContextEditor value={context}
                            onChange={(ctx) => setContext(ctx || null)}
                            groupsToUse={['makes', 'models', 'families', 'submodels']} />
      </span>
      <MiniatureStateSwitch multi value={states} onChange={setStates} />
      <span className={'btn btn-sm btn-light ml-2'} title={'Refresh'} onClick={fetchResults}>↻</span>
    </div>

    {error ? <div className={'alert alert-danger'}>{error}</div> : null}
    {loading ? <div className={'text-muted small mb-2'}>Loading…</div> : null}
    {!loading && !results.length ? <div className={'text-muted'}>No miniatures found for this filter.</div> : null}

    <div className={'d-flex flex-wrap'} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
      {results.map((m) => {
        const url = miniatureImageUrl(m.image);
        const isCurrent = m._id === currentId;
        return <div key={m._id}
                    className={`border rounded p-2 m-1 text-center ${isCurrent ? 'border-primary bg-light-primary' : ''}`}
                    style={{ width: '200px' }}>
          {url
            ? <ScaledMiniature miniature={m} pxPerMm={pxPerMm} crop margin={0} />
            : <div className={'text-muted small py-3'}>no image</div>}

          <div className={'small mt-1'}>
            <LegoContextSummary context={m.context} wrap={false} inline={false}/>
          </div>

          <div className={'my-1'}>
            <span className={`badge badge-${STATE_COLORS[m.state] || 'dark'}`}>{m.state}</span>
          </div>

          <div className={'mb-1'}><BadgeId id={m._id} /></div>

          <IconButton fill className='w-100' icon={isCurrent ? 'check' : 'add_circle_outline'} level={isCurrent ? 'success' : 'primary'} disabled={isCurrent} onClick={() => onSelect(m._id)}>
            {isCurrent ? 'Linked' : 'Select'}
          </IconButton>
        </div>;
      })}
    </div>
  </div>;
}
